/**
 * Number & Place Value game question generators.
 * Uses icons, symbols, place value charts, grids and number lines.
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

// ---- number-read-compare: Reading, writing, comparing, ordering numbers ----
const generateReadCompareQuestion = (difficulty = "normal") => {
    let min = 100, max = 999;
    if (difficulty === "easy") { min = 10; max = 99; }
    else if (difficulty === "hard") { min = 1000; max = 99999; }

    const a = Math.floor(Math.random() * (max - min + 1)) + min;
    const b = Math.floor(Math.random() * (max - min + 1)) + min;
    if (a === b) return generateReadCompareQuestion(difficulty);

    const ops = [
        { sym: "<", check: (x, y) => x < y, label: "less than" },
        { sym: ">", check: (x, y) => x > y, label: "greater than" },
    ];
    const op = ops[Math.floor(Math.random() * ops.length)];
    const answer = op.check(a, b) ? String(a) : String(b);

    const nums = [a, b];
    const wrong = nums.find((n) => String(n) !== answer);
    const distractors = [wrong];
    const step = difficulty === "easy" ? 1 : difficulty === "hard" ? 100 : 10;
    while (distractors.length < 3) {
        const d = parseInt(answer, 10) + (Math.random() < 0.5 ? step : -step);
        if (d > 0 && !distractors.includes(String(d)) && String(d) !== answer) {
            distractors.push(String(d));
        }
    }

    return {
        mode: "compare",
        question: `Which number is ${op.label}? Pick the correct one.`,
        numbers: [a, b],
        symbol: op.sym,
        answer,
        options: shuffle([answer, ...distractors.slice(0, 3)]),
        type: "number-read-compare",
        visualType: "compare-grid",
    };
};

// ---- place-value-rounding: Place value, rounding and estimation ----
const generatePlaceValueQuestion = (difficulty = "normal") => {
    let digits = 3;
    if (difficulty === "easy") digits = 2;
    else if (difficulty === "hard") digits = 4;

    let n = 0;
    for (let i = 0; i < digits; i++) {
        n = n * 10 + (i === 0 ? Math.floor(Math.random() * 9) + 1 : Math.floor(Math.random() * 10));
    }

    const places = ["ones", "tens", "hundreds", "thousands"];
    const idx = Math.floor(Math.random() * Math.min(digits, 4));
    const place = places[idx];
    const divisor = Math.pow(10, idx);
    const digit = Math.floor((n / divisor) % 10);
    const answer = String(digit);

    const distractors = [];
    const used = new Set([digit]);
    while (distractors.length < 3) {
        const d = Math.floor(Math.random() * 10);
        if (!used.has(d)) { used.add(d); distractors.push(String(d)); }
    }

    const pvChart = [];
    let temp = n;
    const labels = ["Ones", "Tens", "Hundreds", "Thousands"];
    for (let i = 0; i < digits; i++) {
        pvChart.push({
            place: labels[i],
            value: temp % 10,
            highlighted: i === idx,
        });
        temp = Math.floor(temp / 10);
    }
    pvChart.reverse();

    return {
        mode: "place-value",
        question: `What is the digit in the ${place} place?`,
        number: n,
        placeValueChart: pvChart,
        answer,
        options: shuffle([answer, ...distractors]),
        type: "place-value-rounding",
        visualType: "place-value-chart",
    };
};

const generateRoundingQuestion = (difficulty = "normal") => {
    let n, roundTo;
    if (difficulty === "easy") {
        n = Math.floor(Math.random() * 90) + 10;
        roundTo = 10;
    } else if (difficulty === "hard") {
        n = Math.floor(Math.random() * 9000) + 1000;
        roundTo = 1000;
    } else {
        n = Math.floor(Math.random() * 900) + 100;
        roundTo = 100;
    }

    const answer = String(Math.round(n / roundTo) * roundTo);
    const step = roundTo;
    const distractors = [];
    const used = new Set([parseInt(answer, 10)]);
    for (let i = -1; i <= 1; i++) {
        const d = parseInt(answer, 10) + i * step;
        if (d >= 0 && !used.has(d)) distractors.push(String(d));
    }
    while (distractors.length < 3) {
        const d = parseInt(answer, 10) + (Math.floor(Math.random() * 3) - 1) * step;
        if (d >= 0 && !distractors.includes(String(d)) && String(d) !== answer) {
            distractors.push(String(d));
        }
    }

    return {
        mode: "rounding",
        question: `Round ${n} to the nearest ${roundTo}.`,
        number: n,
        roundTo,
        answer,
        options: shuffle([answer, ...distractors.slice(0, 3)]),
        type: "place-value-rounding",
        visualType: "number-line",
    };
};

// ---- prime-factors-multiples ----
const isPrime = (n) => {
    if (n < 2) return false;
    for (let i = 2; i * i <= n; i++) if (n % i === 0) return false;
    return true;
};

const getFactors = (n) => {
    const f = [];
    for (let i = 1; i <= n; i++) if (n % i === 0) f.push(i);
    return f;
};

const generatePrimeQuestion = (difficulty = "normal") => {
    const primes = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29];
    const nonPrimes = [4, 6, 8, 9, 10, 12, 14, 15, 16, 18];

    const usePrime = Math.random() < 0.5;
    const pool = usePrime ? primes : nonPrimes;
    const n = pool[Math.floor(Math.random() * pool.length)];
    const answer = usePrime ? "Prime" : "Not prime";

    return {
        mode: "prime-check",
        question: `Is ${n} a prime number?`,
        number: n,
        factors: getFactors(n),
        answer,
        options: shuffle(["Prime", "Not prime"]),
        type: "prime-factors-multiples",
        visualType: "factor-dots",
    };
};

const generateMultiplesQuestion = (difficulty = "normal") => {
    const mult = difficulty === "easy" ? [2, 5, 10][Math.floor(Math.random() * 3)] :
        difficulty === "hard" ? [7, 8, 9, 11][Math.floor(Math.random() * 4)] :
        [3, 4, 6][Math.floor(Math.random() * 3)];

    const k = Math.floor(Math.random() * 8) + 2;
    const answerNum = mult * k;
    const answer = String(answerNum);

    // Distractors must NOT be multiples of mult, so only one option is correct
    const distractors = [];
    const used = new Set([answerNum]);
    const offsets = [-2, -1, 1, 2, 3, -3, 4, -4];
    for (const off of offsets) {
        const d = answerNum + off;
        if (d > 0 && d % mult !== 0 && !used.has(d)) {
            used.add(d);
            distractors.push(String(d));
            if (distractors.length >= 3) break;
        }
    }
    while (distractors.length < 3) {
        const d = Math.floor(Math.random() * (answerNum + 20)) + Math.max(1, answerNum - 15);
        if (d > 0 && d % mult !== 0 && !used.has(d)) {
            used.add(d);
            distractors.push(String(d));
        }
    }

    return {
        mode: "multiples",
        question: `Which is a multiple of ${mult}?`,
        multipleOf: mult,
        answer,
        options: shuffle([answer, ...distractors.slice(0, 3)]),
        type: "prime-factors-multiples",
        visualType: "multiple-grid",
    };
};

// ---- square-cube: Square and cube numbers ----
const generateSquareQuestion = (difficulty = "normal") => {
    let maxBase = 5;
    if (difficulty === "easy") maxBase = 4;
    else if (difficulty === "hard") maxBase = 12;

    const base = Math.floor(Math.random() * (maxBase - 1)) + 2;
    const answer = String(base * base);
    const side = base;

    const distractors = [];
    for (let i = -1; i <= 1; i++) {
        const b = base + i;
        if (b >= 2) {
            const d = b * b;
            if (String(d) !== answer) distractors.push(String(d));
        }
    }
    while (distractors.length < 3) {
        const b = Math.floor(Math.random() * maxBase) + 2;
        const d = String(b * b);
        if (!distractors.includes(d) && d !== answer) distractors.push(d);
    }

    return {
        mode: "square",
        question: `How many squares in this ${side}×${side} grid? (What is ${side}²?)`,
        side,
        gridSize: side * side,
        answer,
        options: shuffle([answer, ...distractors.slice(0, 3)]),
        type: "square-cube",
        visualType: "square-grid",
    };
};

const generateCubeQuestion = (difficulty = "normal") => {
    const base = difficulty === "easy" ? 2 : difficulty === "hard" ? 5 : 3;
    const answer = String(base * base * base);

    const distractors = [];
    for (let i = -1; i <= 1; i++) {
        const b = base + i;
        if (b >= 2) {
            const d = String(b * b * b);
            if (d !== answer) distractors.push(d);
        }
    }

    return {
        mode: "cube",
        question: `What is ${base}³? (${base} × ${base} × ${base})`,
        base,
        answer,
        options: shuffle([answer, ...distractors]),
        type: "square-cube",
        visualType: "cube-visual",
    };
};

// ---- negative-sequences ----
const generateNegativeSequenceQuestion = (difficulty = "normal") => {
    let minStep = 1, maxStep = 5;
    if (difficulty === "easy") maxStep = 3;
    else if (difficulty === "hard") maxStep = 8;

    const step = (Math.random() < 0.5 ? 1 : -1) * (Math.floor(Math.random() * (maxStep - minStep + 1)) + minStep);
    let start = Math.floor(Math.random() * 30) - 10;
    if (difficulty === "hard") start = Math.floor(Math.random() * 50) - 25;

    const seq = [start, start + step, start + step * 2, start + step * 3, start + step * 4];
    const missingIdx = Math.floor(Math.random() * seq.length);
    const answer = String(seq[missingIdx]);

    const displaySeq = seq.map((v, i) => (i === missingIdx ? "?" : String(v)));
    const offsets = [step, -step, step + 1, step - 1];
    const distractors = [];
    const used = new Set([parseInt(answer, 10)]);
    for (const off of offsets) {
        const d = parseInt(answer, 10) + off;
        if (!used.has(d)) { used.add(d); distractors.push(String(d)); }
    }

    return {
        mode: "negative-sequence",
        question: `Find the missing number in the sequence.`,
        sequence: displaySeq,
        sequenceValues: seq,
        answer,
        options: shuffle([answer, ...distractors.slice(0, 3)]),
        type: "negative-sequences",
        visualType: "number-line-sequence",
    };
};

// ---- Export: generate by game key ----
export const generateNumberPlaceValueQuestionByKey = (gameKey, difficulty = "normal") => {
    switch (gameKey) {
        case "number-read-compare":
            return generateReadCompareQuestion(difficulty);
        case "place-value-rounding":
            return Math.random() < 0.6
                ? generatePlaceValueQuestion(difficulty)
                : generateRoundingQuestion(difficulty);
        case "prime-factors-multiples":
            return Math.random() < 0.5
                ? generatePrimeQuestion(difficulty)
                : generateMultiplesQuestion(difficulty);
        case "square-cube":
            return Math.random() < 0.6
                ? generateSquareQuestion(difficulty)
                : generateCubeQuestion(difficulty);
        case "negative-sequences":
            return generateNegativeSequenceQuestion(difficulty);
        default:
            return generateReadCompareQuestion(difficulty);
    }
};
